'use client';

import Link from 'next/link';
import { useRecentlyViewed } from '../_hooks/useRecentlyViewed';
import { bumpPastDates } from '../config';

// JSX/classes ported verbatim from bip_website_designs/src/components/hotels/RecentlyViewed.jsx.
// Only dynamic property data + href is bound.

function bumpedUrl(savedUrl: string): string {
  try {
    const u = new URL(savedUrl, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const ci = u.searchParams.get('checkin');
    const co = u.searchParams.get('checkout');
    if (ci && co) {
      const { checkin, checkout } = bumpPastDates(ci, co);
      u.searchParams.set('checkin', checkin);
      u.searchParams.set('checkout', checkout);
    }
    return `${u.pathname}${u.search}`;
  } catch {
    return savedUrl;
  }
}

export default function HomeRecentlyViewed() {
  const { viewed, hydrated } = useRecentlyViewed();

  if (!hydrated || viewed.length === 0) return null;

  return (
    <section className="bg-yellow-light pt-16">
      <div className="container">
        <h3 className="font-display text-xl md:text-3xl font-bold text-secondary mb-3">
          Your recently viewed properties
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {viewed.slice(0, 6).map((p) => {
            const href = bumpedUrl(p.url);
            const location = [p.city, p.country].filter(Boolean).join(', ');
            return (
              <article key={p.slug} className="recent-view-sec">
                <Link href={href} prefetch={false} className="block h-full">
                  {p.primaryImage ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={p.primaryImage}
                      alt={p.name}
                      className="bevel-shape-r w-full h-full object-cover block"
                      loading="lazy"
                    />
                  ) : (
                    <div className="bevel-shape-r w-full h-full bg-purple-light" aria-hidden="true" />
                  )}
                </Link>
                <div className="bevel-shape-l bg-white p-4 self-stretch">
                  <div className="flex gap-1 text-orange pt-1" aria-label={`${p.starRating} star rating`}>
                    {Array.from({ length: p.starRating }).map((_, i) => (
                      <i key={i} className="fa-solid fa-star text-sm" aria-hidden="true" />
                    ))}
                  </div>
                  <h6 className="mt-2 font-display font-bold text-[16px] text-secondary m-0 mb-1">
                    <Link href={href} prefetch={false} className="hover:text-primary text-secondary no-underline">
                      {p.name}
                    </Link>
                  </h6>
                  {location && <p className="mt-0.5 mb-0 text-sm text-gray-text">{location}</p>}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
